import React, { useMemo } from 'react';
import { Select, SelectDynamic, Slider, Stack, Text, ToggleGroup } from 'shared-components';
import DateFnsUtils from '@date-io/date-fns';
import classNames from 'classnames';
import './inputs.scss';

import {
  FormDescription,
  FormItem,
  FormControl as FormControlUI,
  FormMessage,
} from '@components/shadcn/components/ui/form';
import { cn, Checkbox, Input } from 'shared-components';
import { useDispatch } from 'react-redux';
import { change } from 'redux-form';
import { NumberFormatBase, NumericFormat, PatternFormat } from 'react-number-format';
import { cleanSsnToDigitsAndStars, maskSsn } from '@components/_fields/normalizers';
import { autofillDefaults } from './autofill';

export const Wrapper = (props) => {
  return <div className={classNames(...props.classes)}>{props.children}</div>;
};

export const Label = (props) => {
  if (props.text) {
    return (
      <label className={props.className} htmlFor={props.name}>
        {props.text}
      </label>
    );
  } else return null;
};

export const Error = ({ meta: { error, touched, active }, hide, className }) => {
  // Touched is enough: the old `!pristine` guard meant a required field that
  // was never typed into could not show "Please enter…" at all — neither on
  // blur nor when Continue marks every field touched (mobile UX audit
  // 2026-10, F10). The message still hides while the field is being edited.
  const shouldShowError = error && touched && !active && !hide;
  if (!shouldShowError) {
    return null;
  }

  return (
    <div
      className={cn(
        'error-text max-w-[368px] mx-auto text-center w-full align-center justify-center justify-items-center',
        className
      )}
    >
      <Text variant="error">{error}</Text>
    </div>
  );
};

export const newThemeInput = ({
  meta,
  label,
  labelText,
  labelClassName,
  input,
  className,
  formItemClassName,
  type,
  focusRef,
  placeholder,
  autoFocus,
  autoComplete,
  inputMode,
  tabIndex,
  hideError,
  smallInfoText,
  half,
  defaultValue,
  formatAsNumber,
  formatAsPhone,
  formatAsFederalTaxId,
  formatAsSsn,
  extraNode = null,
  ...otherProps
}) => {
  // Mobile keyboard + autofill (F13): derived from the field name and the
  // format flags unless the caller sets them; 'on' stays the last resort.
  const autofill = autofillDefaults({
    name: input.name,
    type,
    formatAsNumber,
    formatAsPhone,
    formatAsFederalTaxId,
    formatAsSsn,
  });
  const autoCompleteValue = autoComplete || autofill.autoComplete || 'on';
  const inputModeValue = inputMode || autofill.inputMode;
  const typeValue = type || autofill.type;

  const inputBaseProps = {
    ...input,
    ...otherProps,
    id: input.name,
    type: typeValue,
    ref: focusRef,
    placeholder: placeholder || labelText || label,
    autoFocus: autoFocus,
    tabIndex: tabIndex,
    autoComplete: autoCompleteValue,
    inputMode: inputModeValue,
    'data-testid': otherProps['data-testid'],
    'data-hj-whitelist': true,
    className: className,
    label: labelText || label,
  };
  return (
    <FormItem
      className={cn('grid grid-cols-1 w-full align-center justify-center justify-items-center', formItemClassName)}
      {...(half ? { style: { width: '50%' } } : {})}
    >
      <FormControlUI>
        {formatAsNumber ? (
          <NumericFormat customInput={Input} thousandSeparator={true} prefix={'$ '} {...inputBaseProps} />
        ) : formatAsPhone ? (
          <PatternFormat customInput={Input} format="###-###-####" patternChar="#" mask="_" {...inputBaseProps} />
        ) : formatAsFederalTaxId ? (
          <PatternFormat customInput={Input} format="XX-XXXXXXX" patternChar="X" mask="_" {...inputBaseProps} />
        ) : formatAsSsn ? (
          // DOESNT WORK WITH A MASK ****** if using NumberFormatBase.
          // Input only will keep the jumping carret but from redux-form but is better than a broken mask.
          <Input {...inputBaseProps} />
        ) : (
          <NumberFormatBase
            type="text"
            displayType="input"
            format={(value) => (value ? value : '')}
            removeFormatting={(value) => value}
            valueIsNumericString={false}
            customInput={Input}
            {...inputBaseProps}
            getCaretBoundary={(formattedValue, caretPos) => {
              return Array.from({ length: formattedValue.length + 1 }).map(function () {
                return true;
              });
            }}
          />
        )}
      </FormControlUI>
      {smallInfoText && <FormDescription>{smallInfoText}</FormDescription>}
      <Error meta={meta} hide={hideError} />
      <FormMessage />
      {extraNode}
    </FormItem>
  );
};
export const basicInput = ({
  meta,
  label,
  labelText,
  input,
  className,
  type,
  focusRef,
  placeholder,
  autoFocus,
  tabIndex,
  hideError,
  ...otherProps
}) => {
  return (
    <React.Fragment>
      <input
        {...input}
        id={input.name}
        type={type}
        ref={focusRef}
        placeholder={placeholder || labelText || label}
        autoFocus={autoFocus}
        tabIndex={tabIndex}
        data-hj-whitelist
        data-testid={otherProps['data-testid']}
      />
      <Error meta={meta} hide={hideError} />
    </React.Fragment>
  );
};

export const rangeInput = (props) => {
  const { input, min, max, variant, ...otherProps } = props;

  return (
    <Slider
      {...(variant ? { variant } : {})}
      value={[Number(input.value) ?? 50000]}
      onValueChange={(value) => {
        input.onChange(value ? value[0] : 0);
      }}
      id={input.name}
      data-testid={otherProps['data-testid']}
      defaultValue={[Number(input.value) ?? 50000]}
      max={Number(max) ?? 250000}
      min={Number(min) ?? 0}
      step={1000}
    />
  );
};

export const selectDynamicInput = (props) => {
  const { input, label, className, meta, ...otherProps } = props;
  const component = useMemo(
    () => (
      <SelectDynamic
        {...otherProps}
        commandItemProps={{
          ...(otherProps.commandItemProps ?? {}),
          onSelect: (item) => {
            input.onChange(item);
            if (otherProps.commandItemProps?.onSelect) {
              otherProps.commandItemProps.onSelect(item);
            }
          },
        }}
      />
    ),
    []
  );
  return component;
};

export const radioButton = (props) => {
  const { type, input, id, ...otherProps } = props;

  return <input {...input} id={id} type={type} data-testid={otherProps['data-testid']} />;
};

export const checkBox = (props) => {
  const { type, input, id, children, variant, labelProps, onClick = (isChecked) => {}, ...otherProps } = props;
  // The control is a bare button: point it at its visible label so it has an accessible name.
  const labelId = `${id ?? input.name}-label`;
  return (
    <>
      <Checkbox
        className="tento-checkbox"
        variant={variant}
        aria-labelledby={children ? labelId : undefined}
        {...input}
        onBlur={(e) => {
          // Bug that makes true become "true". To keep boolean we need to add this onBlur.
        }}
        onClick={(isChecked) => {
          if (onClick) {
            onClick(isChecked);
          }
          input.onChange(isChecked);
        }}
        type={'button'}
        // Strictly boolean: an untouched field's '' would be rendered as aria-checked="" (no state at all).
        checked={input.value === true || input.value === 'true'}
        id={id}
        data-testid={otherProps['data-testid']}
        stackProps={{
          className: 'w-full',
        }}
        {...otherProps}
        labelProps={{ ...labelProps, id: labelId }}
      >
        {children}
      </Checkbox>
      <Error meta={otherProps.meta} hide={otherProps.hideError} />
    </>
  );
};

const validityClass = (meta) => {
  if (meta.active) {
    return;
  }
  if (meta.touched && meta.invalid) {
    return 'invalid';
  }
  if (meta.touched && meta.valid) {
    return 'valid';
  }
};

// Accessible name for a select trigger: the field name (label, else placeholder) plus the
// selected option. The trigger is a <button role="combobox"> that screen readers announced as
// just "button" whenever it rendered without text (mobile UX audit F4).
const selectTriggerName = ({ label, placeholder, options, value }) => {
  const text = (candidate) =>
    typeof candidate === 'string' && candidate.trim() ? candidate.trim().replace(/\s*\*$/, '') : undefined;
  const fieldName = text(label) ?? text(placeholder);
  const selected =
    value === '' || value === undefined || value === null
      ? undefined
      : options?.find((option) => String(option.value) === String(value))?.text;
  // A rich (non-string) option names the trigger through its own content.
  if (!fieldName || (selected && !text(selected))) return undefined;
  return text(selected) && text(selected) !== fieldName ? `${fieldName}: ${text(selected)}` : fieldName;
};

export const muiCustomSelect = (props) => {
  const { input, label, className, valueAsNumber, selectTriggerProps, ...otherProps } = props;

  // check for 0 value meaning filled in, somewhere??
  const defaultValue = valueAsNumber
    ? input.value || input.value === 0
      ? Number(input.value)
      : undefined
    : input.defaultValue
    ? Number(input.defaultValue)
    : input.defaultValue ?? input.value;
  console.log('muiCustomSelect', defaultValue, input, props);
  return (
    <Select
      {...input}
      {...otherProps}
      defaultValue={defaultValue?.toString()}
      label={label}
      onValueChange={(value) => {
        if (valueAsNumber) {
          input.onChange(Number(value));
        } else {
          input.onChange(value);
        }
      }}
      displayEmpty
      // Center the trigger within its parent so the global
      // `.apply .tento-form-element { max-width: 368px }` rule (from
      // shared-components/app/globals.css) doesn't leave the trigger
      // left-aligned in containers wider than 368px.
      selectTriggerProps={{
        'aria-label': selectTriggerName({
          label,
          placeholder: otherProps.selectValueProps?.placeholder,
          options: otherProps.options,
          value: input.value,
        }),
        ...selectTriggerProps,
        className: cn('mx-auto', selectTriggerProps?.className),
      }}
      // The arrow inside the trigger is decoration: the trigger itself is the control.
      arrowButtonProps={{ 'aria-hidden': true, tabIndex: -1, ...otherProps.arrowButtonProps }}
      // The option list's viewport is the element that scrolls and the options inside are
      // tabindex=-1, so axe scrollable-region-focusable flags it (mobile UX audit 2026-10, Trello
      // #2060). shared-components 8c2b440 makes the viewport focusable itself; until Apply picks
      // up that release, set it here when the content mounts (it mounts on open).
      selectContentProps={{
        ...otherProps.selectContentProps,
        // Radix renders the viewport as role="presentation", which a focusable element may not
        // carry (axe presentation-role-conflict); "group" is the container role a listbox allows.
        ref: (content) => {
          const viewport = content?.querySelector('[data-radix-select-viewport]');
          viewport?.setAttribute('tabindex', '0');
          viewport?.setAttribute('role', 'group');
        },
      }}
      data-testid={`select-${otherProps['data-testid']}`}
      inputProps={{ 'data-testid': otherProps['data-testid'] }}
    >
      {props.children}
    </Select>
  );
};

export const Toggle = (props) => {
  const { input, valueAsNumber, ...otherProps } = props;
  const dispatch = useDispatch();
  return (
    <>
      <ToggleGroup
        type="single"
        {...input}
        {...otherProps}
        onBlur={() => {}}
        defaultValue={
          input.defaultValue
            ? valueAsNumber
              ? Number(input.defaultValue)
              : input.defaultValue
            : otherProps.defaultValue
            ? valueAsNumber
              ? Number(otherProps.defaultValue)
              : otherProps.defaultValue
            : undefined
        }
        className={cn(
          'flex-wrap gap-[3.25px] w-full align-center justify-center justify-items-center mx-auto',
          otherProps.className
        )}
        buttonStackProps={{
          className: 'flex-1 w-full',
        }}
        itemClassName={cn('flex-1', otherProps.itemClassName)}
        value={valueAsNumber ? Number(input.value) : input.value}
        onValueChange={(value) => {
          const valueInProperType = valueAsNumber ? Number(value) : value;
          input.onChange(valueInProperType);
          window.setTimeout(() => dispatch(change('application', input.name, valueInProperType)), 0);
          if (otherProps.onValueChange) otherProps.onValueChange(valueInProperType);
        }}
      />
      <Error meta={otherProps.meta} hide={otherProps.hideError} className="!text-center" />
    </>
  );
};
